/**
 * VS-Code-style path disambiguation — the single source of truth for turning a
 * set of full paths into short, per-item folder labels.
 *
 * For each path it returns the SHORTEST trailing run of ancestor-folder segments
 * (the leaf itself excluded) that is unique among the set — but at minimum the
 * immediate parent, so every item always carries a location for context.
 *
 * - Unique leaf name → its immediate parent folder (short context).
 * - Same-named leaves in different folders → just enough parent path to tell
 *   them apart (e.g. `Reports` in `2024` vs `2025`).
 * - Leaves that sit at the root (no parent) → `''`.
 * - Two items with the *identical* parent path can't be told apart by path;
 *   both fall back to that shared parent (the caller disambiguates another way,
 *   e.g. a count badge).
 *
 * Pure + dependency-free so every UI feature (mini-app access pane, share-links
 * sidebar, …) shares ONE implementation instead of hand-rolled copies.
 *
 * @param paths full paths, each like `/a/b/leaf` (or `root / a / b / leaf`)
 * @param sep   segment separator — `'/'` (default) or `' / '` for pretty paths
 */
export function disambiguateParentLabels(paths: string[], sep = '/'): string[] {
  // Drop the leaf: we describe where each item LIVES, not repeat its own name.
  const parents = paths.map((p) => splitSegments(p, sep).slice(0, -1))
  // Compare on a NUL delimiter that can't occur in a folder name, so a name
  // containing `sep` (or a space) never causes a false suffix match.
  const NUL = '\u0000'
  const key = (segs: string[]) => segs.join(NUL)
  return parents.map((mine, i) => {
    if (mine.length === 0) return ''
    const mineKey = key(mine)
    for (let take = 1; take <= mine.length; take++) {
      const suffix = mine.slice(mine.length - take)
      const suffixKey = key(suffix)
      const collides = parents.some((other, j) => {
        if (j === i) return false
        if (key(other) === mineKey) return false // identical parent → path can't disambiguate
        return key(other.slice(Math.max(0, other.length - take))) === suffixKey
      })
      if (!collides) return suffix.join(sep)
    }
    return mine.join(sep)
  })
}

function splitSegments(path: string, sep: string): string[] {
  return path.split(sep).map((s) => s.trim()).filter(Boolean)
}
